import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Upload, AlertCircle, FileText, Image, File } from "lucide-react";

export type Attachment = {
  file: File;
  description: string;
};

interface FileUploaderProps {
  attachments: Attachment[];
  onAttachmentsChange: (attachments: Attachment[]) => void;
  maxFileSize?: number;
  allowedTypes?: string[];
  label?: string;
  required?: boolean;
  showFileSize?: boolean;
  maxFiles?: number;
}

export function FileUploader({
  attachments,
  onAttachmentsChange,
  maxFileSize = 10,
  allowedTypes = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/jpg",
    "image/gif",
    "image/webp",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/plain",
    "text/csv"
  ],
  label = "Adjuntos",
  required = false,
  showFileSize = true,
  maxFiles
}: FileUploaderProps) {
  const [error, setError] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);

  const validateFile = (file: File): string | null => {
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxFileSize) {
      return `El archivo "${file.name}" excede el tamaño máximo de ${maxFileSize}MB`;
    }

    if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
      return `El tipo de archivo "${file.name}" no está permitido`;
    }

    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError("");
    if (!e.target.files) return;
    processFiles(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    setError("");
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const processFiles = (files: FileList) => {
    const filesArray = Array.from(files);
    
    if (maxFiles && attachments.length + filesArray.length > maxFiles) {
      setError(`Solo puedes subir un máximo de ${maxFiles} archivo(s)`);
      return;
    }

    const validFiles: Attachment[] = [];
    const errors: string[] = [];

    filesArray.forEach(file => {
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(validationError);
      } else {
        validFiles.push({ file, description: "" });
      }
    });

    if (errors.length > 0) {
      setError(errors.join(". "));
    }

    if (validFiles.length > 0) {
      onAttachmentsChange([...attachments, ...validFiles]);
    }
  };

  const removeAttachment = (index: number) => {
    onAttachmentsChange(attachments.filter((_, i) => i !== index));
    setError("");
  };

  const updateDescription = (index: number, description: string) => {
    const updated = attachments.map((att, i) =>
      i === index ? { ...att, description } : att
    );
    onAttachmentsChange(updated);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/")) {
      return <Image className="h-4 w-4 text-blue-600" />;
    }
    if (fileType === "application/pdf") {
      return <FileText className="h-4 w-4 text-red-600" />;
    }
    return <File className="h-4 w-4 text-slate-600" />;
  };

  const getFileTypeLabel = (): string => {
    if (allowedTypes.length === 0) return "Todos los tipos";
    
    const types = new Set<string>();
    allowedTypes.forEach(type => {
      if (type.includes("pdf")) types.add("PDF");
      if (type === "image/jpeg" || type === "image/jpg") types.add("JPG");
      if (type === "image/png") types.add("PNG");
      if (type === "image/gif") types.add("GIF");
      if (type === "image/webp") types.add("WebP");
      if (type.includes("word") || type.includes("document")) types.add("Word");
      if (type.includes("excel") || type.includes("sheet")) types.add("Excel");
      if (type.includes("text/plain")) types.add("Texto");
      if (type.includes("csv")) types.add("CSV");
    });
    
    return Array.from(types).join(", ");
  };

  return (
    <div className="space-y-2">
      <Label>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>

      <div 
        className={`relative flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl transition-all ${
          isDragging 
            ? "border-indigo-500 bg-indigo-50/50" 
            : "border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-slate-400"
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          id="file-upload-input"
          type="file"
          multiple={!maxFiles || maxFiles > 1}
          onChange={handleFileChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          accept={allowedTypes.join(",")}
        />
        <div className="flex flex-col items-center justify-center text-slate-500 pointer-events-none">
          <Upload className={`h-8 w-8 mb-2 ${isDragging ? "text-indigo-500" : "text-slate-400"}`} />
          <p className="text-sm font-medium text-slate-700">
            Haz clic o arrastra un archivo aquí
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {maxFiles && maxFiles === 1 
              ? "1 archivo máximo" 
              : attachments.length > 0 
                ? `${attachments.length} archivo(s) seleccionado(s)${maxFiles ? ` (máx. ${maxFiles})` : ''}` 
                : "Puedes subir múltiples archivos"}
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-700 font-medium">{error}</div>
        </div>
      )}

      <p className="text-xs text-slate-500">
        Tamaño máximo: {maxFileSize}MB • Tipos: {getFileTypeLabel()}
      </p>

      {attachments.length > 0 && (
        <div className="mt-3 space-y-2 border rounded-md p-3 bg-slate-50/50">
          {attachments.map((att, idx) => (
            <div
              key={idx}
              className="flex flex-col md:flex-row md:items-center gap-2 p-3 bg-white rounded-md border border-slate-200 hover:border-slate-300 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {getFileIcon(att.file.type)}
                  <span className="text-sm font-medium truncate">
                    {att.file.name}
                  </span>
                  {showFileSize && (
                    <span className="text-xs text-slate-500 flex-shrink-0">
                      ({formatFileSize(att.file.size)})
                    </span>
                  )}
                </div>
              </div>
              <Input
                placeholder="Descripción del archivo (opcional)"
                value={att.description}
                onChange={(e) => updateDescription(idx, e.target.value)}
                className="md:w-64"
              />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                onClick={() => removeAttachment(idx)}
                title="Eliminar archivo"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}